import React, { useEffect } from "react";
import jexcel, { JExcelElement } from "jexcel";

import "../../../../node_modules/jexcel/dist/jexcel.css";
import { ITeacher, ITeacherForRegister } from "../../../state";
import { csvEscape, parseCsv } from "../../../libs/csvUtils";
import { ModalContext } from "../../../libs/ModalProvider";
import Confirm from "../../../molecules/Confirm";
import AlertModal from "../../../molecules/AlertModal";

import styles from "./TeacherSpreadSheet.module.scss";

export interface TeacherSpreadSheetProps {
  title: string;
  calling: boolean;
  error: string;
  teachers: ITeacher[];
  save: (Teachers: ITeacherForRegister[]) => void;
}
const Fields = {} as { [key: string]: number };
Fields["ID"] = 0;
Fields["姓"] = 1;
Fields["名"] = 2;
Fields["姓(カナ)"] = 3;
Fields["名(カナ)"] = 4;
Fields["メールアドレス"] = 5;
Fields["学校管理者"] = 6;
Fields["授業報告"] = 7;
Fields["顔写真"] = 8;
Fields["チェック結果"] = 9;

const INITIAL_ROW_VALUES = [
  "",
  null,
  null,
  null,
  null,
  null,
  false,
  true,
  null,
  null,
];

const parseCsvBool = (value: string): boolean =>
  (value || "").trim().toLowerCase() === "true";

const teachersToTableData = (teachers: ITeacher[]) =>
  teachers.length
    ? teachers.map(teacher => [
        teacher.id,
        teacher.last_name,
        teacher.first_name,
        teacher.last_name_kana,
        teacher.first_name_kana,
        teacher.email,
        teacher.is_user_manager,
        teacher.receive_email_enabled,
        null,
        null,
      ])
    : [[...INITIAL_ROW_VALUES]];

export const TeacherSpreadSheet = (props: TeacherSpreadSheetProps) => {
  const ctx = React.useContext(ModalContext);
  const jexcelRef = React.useRef<HTMLDivElement>(null);
  const CSVInputRef = React.useRef<HTMLInputElement>(null);
  const [state, setState] = React.useState({
    sheet: null as null | JExcelElement,
    tableData: teachersToTableData(props.teachers) as
      | null
      | (string | number | boolean | null)[][],
    csvFile: null as null | File,
  });
  const requiredFields = React.useMemo(() => {
    return ["姓", "名", "姓(カナ)", "名(カナ)", "メールアドレス"];
  }, []);
  const save = props.save;
  const colWidths = React.useMemo(
    () => [80, 100, 100, 100, 100, 140, 80, 80, 200, 300],
    [],
  );
  const regexCheckEmail = React.useMemo(() => /\S+@\S+\.\S+/, []);
  const cols = React.useMemo(
    () => [
      { index: 0, title: "ID" },
      { index: 1, title: "姓 ※必須" },
      { index: 2, title: "名 ※必須" },
      { index: 3, title: "姓(カナ) ※必須" },
      { index: 4, title: "名(カナ) ※必須" },
      { index: 5, title: "メールアドレス ※必須" },
      { index: 6, title: "学校管理者" },
      { index: 7, title: "授業報告" },
    ],
    [],
  );

  const showErrorMessage = React.useCallback(
    (message: string) => {
      if (CSVInputRef.current) {
        CSVInputRef.current.value = "";
      }
      ctx.actions.showModal(
        <Confirm
          content={message}
          closeModal={ctx.actions.closeModal}
          callback={ctx.actions.closeModal}
        />,
      );
    },
    [ctx.actions],
  );

  const exportCSV = React.useCallback(() => {
    const headers = cols.map(col => col.title);
    const exampleRow = [
      "",
      "例：山田",
      "例：太郎",
      "例：ヤマダ",
      "例：タロウ",
      "yamada@example.com",
      "学校管理者にする場合は「TRUE」、そうでない場合は「FALSE」と入力してください。",
      "授業報告を受け取る場合は「TRUE」、不要な場合は「FALSE」と入力してください。",
    ];
    const bodyRows = props.teachers.map(teacher => [
      `${teacher.id}`,
      teacher.last_name,
      teacher.first_name,
      teacher.last_name_kana,
      teacher.first_name_kana,
      teacher.email,
      teacher.is_user_manager ? "TRUE" : "FALSE",
      teacher.receive_email_enabled ? "TRUE" : "FALSE",
    ]);
    const lines = [
      headers.map(csvEscape).join(","),
      exampleRow.map(csvEscape).join(","),
      ...bodyRows.map(row => row.map(csvEscape).join(",")),
    ].join("\n");
    const BOM = String.fromCharCode(0xfeff);
    const blob = new Blob([BOM + lines + "\n"], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "teacher_list.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [cols, props.teachers]);

  const handleImportCSV = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target?.files ? event.target.files[0] : null;
    if (!file) {
      return;
    }
    const reader = new FileReader();
    reader.readAsText(file);
    reader.onload = e => {
      try {
        const rows = parseCsv((e.target?.result as string).trim());
        if (rows.length === 0) {
          showErrorMessage("無効なCSV形式です");
          return;
        }
        const headerRow = rows[0];
        const expectedColumns = cols.map(col => col.title);
        const isValidHeader =
          headerRow.length === expectedColumns.length &&
          headerRow.every((col, index) => col === expectedColumns[index]);
        if (!isValidHeader) {
          showErrorMessage(
            "無効な列形式です。適切な列を含む CSV を追加してください",
          );
          return;
        }
        const dataRows = rows
          .slice(1)
          .filter(row => !/^例[:：]/.test(row[1] || ""));
        for (const row of dataRows) {
          if (!row[1]) {
            showErrorMessage("「姓」が不足している先生がいます。");
            return;
          }
          if (!row[2]) {
            showErrorMessage("「名」が不足している先生がいます。");
            return;
          }
          if (!row[3]) {
            showErrorMessage("「姓(カナ)」が不足している先生がいます。");
            return;
          }
          if (!row[4]) {
            showErrorMessage("「名(カナ)」が不足している先生がいます。");
            return;
          }
          if (!row[5]) {
            showErrorMessage("「メールアドレス」が不足している先生がいます。");
            return;
          }
          if (!regexCheckEmail.test(row[5])) {
            showErrorMessage("不正なメールアドレスが入力されています");
            return;
          }
        }
        if (dataRows.length === 0) {
          showErrorMessage("登録できる先生のデータがありません。");
          return;
        }
        const importedIds = dataRows
          .filter(row => row[0])
          .map(row => Number(row[0]))
          .sort((a, b) => a - b);
        const existingIds = props.teachers.map(t => t.id).sort((a, b) => a - b);
        if (importedIds.join(",") !== existingIds.join(",")) {
          showErrorMessage(
            "IDが一致しませんでした。既存行のIDは変更せず、新規追加する行はID列を空欄にしてください。",
          );
          return;
        }
        const tableData = dataRows.map(row => [
          row[0] ? Number(row[0]) : "",
          row[1],
          row[2],
          row[3],
          row[4],
          row[5],
          parseCsvBool(row[6]),
          parseCsvBool(row[7]),
          null,
          null,
        ]);
        setState(prevState => ({ ...prevState, tableData, csvFile: file }));
      } catch (error) {
        showErrorMessage("何か問題が発生しました。もう一度試してください。");
      }
    };
    reader.onerror = () => {
      showErrorMessage("何か問題が発生しました。もう一度試してください。");
    };
  };

  useEffect(() => {
    const jexcelRefCurrent = jexcelRef.current;
    if (jexcelRefCurrent) {
      jexcelRefCurrent.innerHTML = "";
    }
    let sheet = null as null | JExcelElement;
    const options = {
      data: state.tableData,
      colWidths,
      columns: [
        { type: "text" as const, title: "ID", readOnly: true },
        { type: "text" as const, title: "姓*" },
        { type: "text" as const, title: "名*" },
        { type: "text" as const, title: "姓(カナ)*" },
        { type: "text" as const, title: "名(カナ)*" },
        { type: "text" as const, title: "メールアドレス*" },
        { type: "checkbox" as const, title: "学校管理者" },
        { type: "checkbox" as const, title: "授業報告" },
        { type: "image" as const, title: "顔写真" },
        { type: "text" as const, title: "チェック結果" },
      ],
      onchange: (
        _instance: HTMLElement,
        _cell: HTMLTableCellElement,
        x: number,
        y: number,
        value: any,
      ) => {
        if (x === Fields["チェック結果"]) {
          return;
        }
        // 顔写真フィールドの場合、無効な値を即座にクリアする
        if (x === Fields["顔写真"] && value) {
          const isValidBase64Image = /^data:image\/[a-z]+;base64,.+/.test(
            value as string,
          );
          if (!isValidBase64Image) {
            sheet?.setValueFromCoords(x, y, "", false);
            sheet?.setValueFromCoords(
              Fields["チェック結果"],
              y,
              "顔写真は画像のみ対応しています",
              false,
            );
            return;
          }
        }
        const rec = sheet?.getRowData(Number(y));
        let result = "";
        if (rec) {
          if (
            rec[Fields["メールアドレス"]] &&
            !regexCheckEmail.test(rec[Fields["メールアドレス"]])
          ) {
            result = "不正なメールアドレスが入力されています";
          } else {
            for (const rF of requiredFields) {
              if (!rec[Fields[rF]]) {
                result = `${rF}が必要です`;
                break;
              }
            }
          }
          sheet?.setValueFromCoords(
            Fields["チェック結果"],
            Number(y),
            result,
            false,
          );
        }
      },
      oninsertrow: (
        _el: HTMLElement,
        rowNumber: number,
        _numOfRows: number,
        _rowRecords: any[],
        _insertBefore: boolean,
      ) => {
        sheet?.setRowData(rowNumber + 1, INITIAL_ROW_VALUES);
      },
    };
    sheet = jexcel(jexcelRefCurrent as any, options as any);
    setState(s => ({ ...s, sheet }));
  }, [state.tableData, colWidths, requiredFields, regexCheckEmail]);

  const addRow = React.useCallback(() => {
    state.sheet?.insertRow();
  }, [state.sheet]);

  const resetFunc = React.useCallback(() => {
    if (CSVInputRef.current) {
      CSVInputRef.current.value = "";
    }
    setState(prevState => ({
      ...prevState,
      tableData: teachersToTableData(props.teachers),
      csvFile: null,
    }));
  }, [props.teachers]);

  const saveFunc = React.useCallback(() => {
    const data = [] as ITeacherForRegister[];
    let isInvalidEmail = false;
    if (state.sheet) {
      const dataSet = state.sheet?.getData();
      dataSet.forEach(d => {
        const isAllRequiredFieldsValid = requiredFields.every(
          rF => !!d[Fields[rF]],
        );
        const emailValue = d[Fields["メールアドレス"]]
          ? (d[Fields["メールアドレス"]] as string)
          : "";
        if (emailValue && !regexCheckEmail.test(emailValue)) {
          isInvalidEmail = true;
        }

        if (isAllRequiredFieldsValid && !d[Fields["チェック結果"]]) {
          const idValue = d[Fields["ID"]];
          data.push({
            id: idValue ? Number(idValue) : undefined,
            last_name: d[Fields["姓"]] as string,
            first_name: d[Fields["名"]] as string,
            last_name_kana: d[Fields["姓(カナ)"]] as string,
            first_name_kana: d[Fields["名(カナ)"]] as string,
            email: d[Fields["メールアドレス"]] as string,
            is_user_manager: d[Fields["学校管理者"]] as boolean,
            receive_email_enabled: d[Fields["授業報告"]] as boolean,
            photo: d[Fields["顔写真"]] as string,
          });
        }
      });
    }
    if (isInvalidEmail) {
      ctx.actions.showModal(
        <AlertModal
          closeModal={() => {
            ctx.actions.closeModal();
          }}
          content={
            <>
              <div>更新できませんでした。</div>
              <div>正しいメールアドレスを入力してください。</div>
            </>
          }
          callback={() => {
            ctx.actions.closeModal();
          }}
        />,
      );
    } else {
      save(data);
    }
  }, [save, state.sheet, regexCheckEmail, requiredFields, ctx.actions]);

  const handleSaveClick = React.useCallback(() => {
    ctx.actions.showModal(
      <Confirm
        calling={props.calling}
        closeModal={() => ctx.actions.closeModal()}
        content={
          <>
            <p className="mb-0">先生を登録・更新します、よろしいですか？</p>
            <p>
              新しく追加した先生やメールアドレスを変更した先生には招待メールが送信されます
            </p>
          </>
        }
        callback={saveFunc}
      />,
    );
  }, [props.calling, ctx.actions, saveFunc]);

  return (
    <div className={styles.wrapper}>
      {props.error && (
        <div
          style={{
            padding: "20px",
            border: "2px solid #d84429",
            lineHeight: "1rem",
            marginBottom: "20px",
          }}
        >
          {props.error}
        </div>
      )}
      <div>
        <h4>{props.title}</h4>
        <p className={styles.note}>
          既存の先生の編集と、新しい先生の追加が一括で行えます。
          <br />
          既存の先生はID列が入った状態で表示されます。新しく追加する場合はID列を空欄のまま入力してください。
          <br />
          ※顔写真を「ダブルクリック」すると画像をアップロードできます。
          <br />
          ※顔写真はCSVでは取り込めません。CSV取込後にシート上で設定してください。
        </p>
      </div>
      <div className={styles.actionButtons}>
        <button
          className={`btn btn-outline-danger me-3`}
          disabled={props.calling}
          onClick={() => {
            ctx.actions.showModal(
              <Confirm
                calling={props.calling}
                content={"編集内容をリセットして元の状態に戻しますか？"}
                closeModal={() => {
                  ctx.actions.closeModal();
                }}
                callback={resetFunc}
              />,
            );
          }}
        >
          リセット
        </button>
        <button
          className={`btn btn-outline me-3 ${styles.utilBtn}`}
          type="button"
          onClick={() =>
            ctx.actions.showModal(
              <Confirm
                content={
                  <span>
                    CSVファイルをエクスポートします。
                    <br />
                    <br />
                    先生データを新規追加する際は、エクスポートしたCSVファイル内の先生データを残したまま、最下行からデータを追加し「ID」は空欄としてください。2行目は記入例となりますので消さないでください。
                  </span>
                }
                closeModal={() => {
                  ctx.actions.closeModal();
                }}
                callback={exportCSV}
              />,
            )
          }
        >
          {props.teachers.length === 0
            ? "インポート用CSVをダウンロード"
            : "CSVエクスポート"}
        </button>
        <button
          type="button"
          className={`btn btn-outline-primary ${styles.importCsv}`}
          onClick={() => CSVInputRef.current?.click()}
        >
          CSVインポート
          <input
            ref={CSVInputRef}
            type="file"
            accept=".csv"
            onChange={handleImportCSV}
          />
        </button>
      </div>
      {state.csvFile && <p className={styles.fileName}>{state.csvFile.name}</p>}
      <div className={styles.excel} ref={jexcelRef} />
      <p className={styles.note}>
        ※「チェック結果」にエラーが表示されている行は登録されませんので、エラーを修正してから登録してください。
      </p>
      <div className={styles.action}>
        <button className="btn btn-primary" onClick={addRow}>
          追加
        </button>
        <button
          className="btn btn-primary"
          onClick={handleSaveClick}
          disabled={props.calling}
        >
          登録
        </button>
      </div>
    </div>
  );
};

export default TeacherSpreadSheet;
